import { useState, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Icon, type IconName } from '@/components/common/Icon';

export interface PatientHeaderAction {
  key: string;
  iconName: IconName;
  label: string;
  onSelect: () => void;
  isDanger: boolean;
}

interface HeaderActionsOverflowMenuProps {
  actions: PatientHeaderAction[];
}

export function HeaderActionsOverflowMenu({ actions }: HeaderActionsOverflowMenuProps) {
  const { t } = useTranslation('patients');
  const [isOpen, setIsOpen] = useState(false);

  function closeMenu() {
    setIsOpen(false);
  }

  function toggleMenu() {
    setIsOpen(function invert(previous) {
      return previous === false;
    });
  }

  function handleKeyDown(keyboardEvent: KeyboardEvent<HTMLDivElement>) {
    if (keyboardEvent.key === 'Escape') {
      closeMenu();
    }
  }

  return (
    <div className="relative" onKeyDown={handleKeyDown}>
      <Button
        variant="outline"
        size="chip"
        aria-haspopup="menu"
        aria-expanded={isOpen}
        onClick={toggleMenu}
      >
        <Icon name="MoreHorizontal" />
        {t('detail.more')}
      </Button>
      {isOpen === true ? (
        <>
          <button
            type="button"
            aria-hidden="true"
            tabIndex={-1}
            className="fixed inset-0 z-40 cursor-default"
            onClick={closeMenu}
          />
          <div
            role="menu"
            className="absolute right-0 top-full z-50 mt-1 min-w-[200px] rounded-xl border border-zooborder bg-zoowhite py-1 shadow-lg"
          >
            {actions.map(function renderAction(action) {
              return (
                <button
                  key={action.key}
                  type="button"
                  role="menuitem"
                  onClick={function select() {
                    closeMenu();
                    action.onSelect();
                  }}
                  className={
                    'flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors hover:bg-zoovpalelavender ' +
                    (action.isDanger === true ? 'text-zooerrortext' : 'text-zooindigo')
                  }
                >
                  <Icon name={action.iconName} />
                  {action.label}
                </button>
              );
            })}
          </div>
        </>
      ) : null}
    </div>
  );
}
